import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { AlertBanner } from '@flowcore-water/ui';
import { useAuth } from '../../contexts/AuthContext';
import { libraryService } from '../../services/libraryService';
import { useData } from '../../contexts/DataContext';
import { SKILL_TREES } from '../../data/skillTrees';
import ReactMarkdown from 'react-markdown';
import type { LibraryItem, LibraryItemType } from '../../types';
import { useTheme } from '@flowcore-water/ui';

const TYPE_LABELS: Record<LibraryItemType, string> = {
  article: 'Article',
  pdf: 'Attachment',
  weblink: 'Web Link',
};

const TYPE_STYLES: Record<LibraryItemType, { bg: string; color: string; borderColor: string }> = {
  article: { bg: 'rgba(34,211,238,0.12)', color: '#22d3ee', borderColor: 'rgba(34,211,238,0.3)' },
  pdf: { bg: 'rgba(245,158,11,0.12)', color: '#fbbf24', borderColor: 'rgba(245,158,11,0.3)' },
  weblink: { bg: 'rgba(139,92,246,0.12)', color: '#a78bfa', borderColor: 'rgba(139,92,246,0.3)' },
};

const TYPE_ICONS: Record<LibraryItemType, React.ReactNode> = {
  article: (
    <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/><polyline points="10 9 9 9 8 9"/></svg>
  ),
  pdf: (
    <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48"/></svg>
  ),
  weblink: (
    <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><line x1="2" y1="12" x2="22" y2="12"/><path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/></svg>
  ),
};

export const LibraryList: React.FC = () => {
  const { t } = useTheme();
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { qualifications: qualificationsData } = useData();
  const [items, setItems] = useState<LibraryItem[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [typeFilter, setTypeFilter] = useState<LibraryItemType | 'all'>('all');
  const [treeFilter, setTreeFilter] = useState<string>('all');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Track IDs that were just soft-deleted (passed via navigate state)
  const justDeletedId = (location.state as any)?.deletedId as string | undefined;

  useEffect(() => {
    const loadItems = async () => {
      try {
        setLoading(true);
        const data = await libraryService.getAll();
        console.log('[LibraryList] Loaded', data.length, 'items from getAll');
        if (justDeletedId) {
          console.log('[LibraryList] Filtering out justDeletedId:', justDeletedId);
        }
        setItems(data);
        setError(null);
      } catch (err) {
        console.error('Error loading library:', err);
        setError('Failed to load library');
      } finally {
        setLoading(false);
      }
    };
    loadItems();
  }, [justDeletedId]);

  const filteredItems = items.filter((item) => {
    // Optimistic filter: hide item that was just soft-deleted even if server hasn't caught up
    if (justDeletedId && item.id === justDeletedId) return false;
    // Training manual weeks are listed on the Water Well Technician Training page
    if (item.manualSection) return false;
    const term = searchTerm.toLowerCase();
    const matchesSearch =
      item.title.toLowerCase().includes(term) ||
      item.description.toLowerCase().includes(term) ||
      (item.type === 'article' && item.body.toLowerCase().includes(term));
    const matchesType = typeFilter === 'all' || item.type === typeFilter;
    const matchesTree = treeFilter === 'all' || (item.skillTreeIds || []).includes(treeFilter);
    return matchesSearch && matchesType && matchesTree;
  });

  const getLinkedTreeNames = (treeIds: string[]): string[] => {
    return treeIds
      .map((id) => SKILL_TREES.find((t) => t.id === id)?.shortName)
      .filter(Boolean) as string[];
  };

  const getLinkedQualNames = (qualIds: string[]): string[] => {
    return qualIds
      .map((id) => qualificationsData.find((q) => q.id === id)?.shortName)
      .filter(Boolean) as string[];
  };

  const canEdit = user?.role === 'instructor' || user?.role === 'admin';

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold " style={{ color: t.textPrimary }}>Library</h1>
          <p className="mt-2 " style={{ color: t.textMuted }}>
            Browse training materials, documents, and resources
          </p>
        </div>
        {canEdit && (
          <button
            onClick={() => navigate('/library/new')}
            className="rounded-lg bg-cyan-600 px-4 py-2 font-medium  hover:bg-cyan-500 transition-colors" style={{ color: t.textPrimary }}
          >
            New Item
          </button>
        )}
      </div>

      {/* Search + Filter */}
      <div className="flex flex-wrap gap-3">
        <input
          type="text"
          placeholder="Search library..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="flex-1 min-w-[200px] rounded-lg border   px-4 py-3  placeholder-slate-500 focus:border-cyan-400 focus:outline-none" style={{ color: t.textPrimary }}
        />
        <div className="flex gap-1">
          {(['all', 'article', 'pdf', 'weblink'] as const).map((type) => (
            <button
              key={type}
              onClick={() => setTypeFilter(type)}
              className="rounded-lg px-3 py-2 text-sm font-medium transition-colors"
              style={typeFilter === type
                ? { backgroundColor: t.accentDim, color: t.textPrimary }
                : { color: t.textMuted }}
            >
              {type === 'all' ? 'All' : TYPE_LABELS[type]}
            </button>
          ))}
        </div>
        {SKILL_TREES.length > 0 && (
          <select
            value={treeFilter}
            onChange={(e) => setTreeFilter(e.target.value)}
            className="rounded-lg border   px-3 py-2 text-sm  focus:border-cyan-400 focus:outline-none" style={{ color: t.textPrimary }}
          >
            <option value="all">All Trees</option>
            {SKILL_TREES.map((tree) => (
              <option key={tree.id} value={tree.id}>{tree.shortName}</option>
            ))}
          </select>
        )}
      </div>

      {/* Results */}
      {loading ? (
        <div className="" style={{ color: t.textMuted }}>Loading library...</div>
      ) : error ? (
        <AlertBanner variant="error" message={error} />
      ) : filteredItems.length === 0 ? (
        <div className="rounded-lg border   px-6 py-8 text-center">
          <p className="" style={{ color: t.textMuted }}>
            {items.length === 0
              ? 'Library is empty. Start by creating an item!'
              : 'No items match your search.'}
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3">
          {filteredItems.map((item) => {
            const linkedNames = getLinkedQualNames(item.linkedQualIds);
            const treeNames = getLinkedTreeNames(item.skillTreeIds || []);
            const typeStyle = TYPE_STYLES[item.type];

            return (
              <Link
                key={item.id}
                to={`/library/${item.slug}`}
                className="group flex flex-col rounded-xl border   overflow-hidden hover:border-cyan-400/50 hover: transition-colors"
                style={{ aspectRatio: '3 / 3.5' }}
              >
                {/* Top section — mini preview or icon + type badge */}
                <div className="flex-1 flex flex-col items-center justify-center p-2 relative overflow-hidden" style={{ backgroundColor: typeStyle.bg }}>
                  {item.type === 'article' && item.body ? (
                    <div className="w-full h-full overflow-hidden pointer-events-none select-none" style={{ color: t.textSecondary, fontSize: '7.5px', lineHeight: '1.4' }}>
                      <ReactMarkdown
                        components={{
                          h1: (props: any) => <div className="font-bold mb-0.5" style={{ color: t.textSecondary, fontSize: '9.5px' }} {...props} />,
                          h2: (props: any) => <div className="font-bold mb-0.5" style={{ color: t.textSecondary, fontSize: '8.5px' }} {...props} />,
                          h3: (props: any) => <div className="font-semibold mb-0.5" style={{ color: t.textSecondary, fontSize: '8px' }} {...props} />,
                          p: (props: any) => <div className="mb-0.5 " style={{ color: t.textMuted }} {...props} />,
                          ul: (props: any) => <div className="mb-0.5 " style={{ color: t.textMuted }} {...props} />,
                          ol: (props: any) => <div className="mb-0.5 " style={{ color: t.textMuted }} {...props} />,
                          li: (props: any) => <div className="" style={{ color: t.textMuted }} {...props} />,
                          a: (props: any) => <span className="" style={{ color: t.accent }} {...props} />,
                          code: (props: any) => <span className="text-cyan-300" {...props} />,
                          blockquote: (props: any) => <div className="border-l border-cyan-600 pl-1 " style={{ color: t.textMuted }} {...props} />,
                          img: () => null,
                        }}
                      >
                        {item.body.slice(0, 750)}
                      </ReactMarkdown>
                    </div>
                  ) : item.type === 'pdf' && item.fileUrl ? (
                    /\.(png|jpe?g|gif|webp|svg|bmp)(\?|$)/i.test(item.fileUrl) ? (
                      <img src={item.fileUrl} alt="" className="w-full h-full object-cover pointer-events-none" />
                    ) : (
                      <div className="w-full h-full overflow-hidden pointer-events-none relative">
                        <iframe src={item.fileUrl} title="" className="absolute border-0" style={{ top: 0, left: '-50%', width: '200%', height: '200%', transform: 'scale(0.5)', transformOrigin: 'top center' }} tabIndex={-1} />
                      </div>
                    )
                  ) : item.type === 'weblink' && item.screenshotUrl ? (
                    <img src={item.screenshotUrl} alt="" className="w-full h-full object-cover pointer-events-none" />
                  ) : (
                    <div className="opacity-60 group-hover:opacity-100 transition-opacity" style={{ color: typeStyle.color }}>
                      {TYPE_ICONS[item.type]}
                    </div>
                  )}
                  <span
                    className="absolute top-1.5 right-1.5 text-[9px] font-bold rounded-full px-1.5 py-0.5 border backdrop-blur-sm"
                    style={{ color: '#fff', borderColor: typeStyle.borderColor, backgroundColor: typeStyle.bg }}
                  >
                    {TYPE_LABELS[item.type]}
                  </span>
                </div>

                {/* Bottom section — title + meta */}
                <div className="p-3 flex flex-col gap-1.5 min-h-0">
                  <h3 className="text-sm font-semibold  line-clamp-2 leading-tight" style={{ color: t.textPrimary }}>
                    {item.title}
                  </h3>
                  {item.description && (
                    <p className="text-xs  line-clamp-2 leading-snug" style={{ color: t.textMuted }}>
                      {item.description}
                    </p>
                  )}
                  <div className="mt-auto pt-1">
                    <p className="text-[10px] " style={{ color: t.textMuted }}>
                      v{item.version} • {new Date(item.updatedAt).toLocaleDateString()}
                    </p>
                    {(treeNames.length > 0 || linkedNames.length > 0) && (
                      <div className="flex flex-wrap gap-1 mt-1">
                        {treeNames.map((name, idx) => (
                          <span
                            key={`tree-${idx}`}
                            className="text-[10px] rounded-full bg-emerald-900/40  px-1.5 py-0.5" style={{ color: t.success }}
                          >
                            {name}
                          </span>
                        ))}
                        {linkedNames.map((name, idx) => (
                          <span
                            key={`qual-${idx}`}
                            className="text-[10px] rounded-full   px-1.5 py-0.5" style={{ color: t.accent }}
                          >
                            {name}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </Link>
            );
          })}
        </div>
      )}
    </div>
  );
};
